import { useEffect, useState } from "react";
import { X, Send, Users, Gift, MessageCircle } from "lucide-react";
import { TELEGRAM_URL, TELEGRAM_HANDLE } from "@/lib/socialLinks";

// Telegram invite popup — shows once, AFTER the new-user welcome popup has
// been dismissed (never two popups stacked on top of each other).
const TG_KEY = "vts:tg-invite:v1";
const WELCOME_KEY = "vts:home-welcome:v1";

const TelegramInvitePopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const check = () => {
      try {
        if (localStorage.getItem(TG_KEY) === "1") return false;
        if (localStorage.getItem(WELCOME_KEY) !== "1") return false; // welcome first
      } catch { return false; }
      // give the visitor ~8s to browse before inviting them
      setTimeout(() => setOpen(true), 8000);
      return true;
    };
    if (check()) return undefined;
    const iv = setInterval(() => { if (check()) clearInterval(iv); }, 1500);
    return () => clearInterval(iv);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(TG_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div data-testid="tg-invite-popup" className="fixed inset-0 z-[85] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-sm rounded-2xl border border-sky-400/40 bg-vts-surface shadow-2xl shadow-sky-500/20 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-vts-line bg-sky-500/10">
          <Send className="h-4 w-4 text-sky-400" />
          <span className="text-sm font-bold text-white font-heading">Join the VTS Telegram</span>
          <button data-testid="tg-invite-close" onClick={dismiss} aria-label="Close"
            className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface text-vts-silver/60 hover:text-white transition">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[13px] text-vts-silver/85 leading-relaxed">
            Everything happens in the group — don't trade alone:
          </p>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2.5 text-[12px] text-vts-silver/85">
              <Users className="h-4 w-4 text-sky-400 shrink-0" />
              <span><b className="text-white">Daily signals & bot tips</b> — strategy talk and live results.</span>
            </div>
            <div className="flex items-center gap-2.5 text-[12px] text-vts-silver/85">
              <Gift className="h-4 w-4 text-amber-300 shrink-0" />
              <span><b className="text-white">More ways to earn</b> — referral contests, prizes & community challenges.</span>
            </div>
            <div className="flex items-center gap-2.5 text-[12px] text-vts-silver/85">
              <MessageCircle className="h-4 w-4 text-vts-profit shrink-0" />
              <span><b className="text-white">Talk to me directly</b> — questions about deposits, bots or anything else.</span>
            </div>
          </div>
          <a data-testid="tg-invite-join-link" href={TELEGRAM_URL} target="_blank" rel="noreferrer" onClick={dismiss}
            className="flex items-center justify-center gap-2 w-full rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-bold text-sm py-3 transition">
            <Send className="h-4 w-4" /> Join {TELEGRAM_HANDLE}
          </a>
          <button data-testid="tg-invite-later-btn" onClick={dismiss}
            className="w-full text-center text-[11px] text-vts-silver/55 hover:text-white transition py-1">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelegramInvitePopup;
